window.AdminNewsletter = function AdminNewsletter({ showNotification }) {
  const [subscribers, setSubscribers] = React.useState([]);
  const [blogs, setBlogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState('dispatcher'); // 'dispatcher' or 'subscribers'
  
  // Search & Filter
  const [searchQuery, setSearchQuery] = React.useState('');
  const [newEmailInput, setNewEmailInput] = React.useState('');
  const [isAddingSubscriber, setIsAddingSubscriber] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);

  // Broadcast Form State
  const [broadcastType, setBroadcastType] = React.useState('blog'); // 'blog', 'newsletter', 'greeting', 'custom'
  const [senderName, setSenderName] = React.useState('NOVATEK Engineering Bureau');
  const [senderEmail, setSenderEmail] = React.useState('newsletter@novatekindustrial.com');
  const [selectedBlogId, setSelectedBlogId] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [targetAudience, setTargetAudience] = React.useState('all');
  const [customBody, setCustomBody] = React.useState('');
  
  // Sending Modal & Animation
  const [isSending, setIsSending] = React.useState(false);
  const [sendingProgress, setSendingProgress] = React.useState(0);
  const [sendingStatusText, setSendingStatusText] = React.useState('');
  const [sendSuccessReport, setSendSuccessReport] = React.useState(null);

  React.useEffect(() => {
    fetchSubscribers();
    fetchBlogs();
    const handleSync = () => {
      fetchSubscribers();
      fetchBlogs();
    };
    window.addEventListener('storage', handleSync);
    window.addEventListener('novatek_subscribers_updated', handleSync);
    return () => {
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('novatek_subscribers_updated', handleSync);
    };
  }, []);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('subscribers.php');
      if (res && res.success) {
        setSubscribers(res.data || []);
        setSelectedIds([]);
      }
    } catch (e) {
      console.warn('Subscribers fetch warning:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogs = async () => {
    try {
      const res = await window.apiClient.get('blogs.php?admin=1');
      if (res && res.success) {
        const published = (res.data || []).filter(b => b.status === 'published');
        setBlogs(published);
        if (published.length > 0 && !selectedBlogId) {
          setSelectedBlogId(published[0].id);
          applyBlogTemplate(published[0]);
        }
      }
    } catch (e) {}
  };

  const applyBlogTemplate = (blog) => {
    if (!blog) return;
    setSubject(`[Novatek Technical Hub] ${blog.title}`);
    setCustomBody(`Dear Engineer / Partner,

We are pleased to publish our latest technical engineering article on the Novatek Hub:

"${blog.title}"
By ${blog.author} (${blog.category})

EXCERPT & SUMMARY:
${blog.summary}

Full technical specifications, vector load diagrams, and telemetry benchmarks are now live on our website.

Click below to read the complete publication on our portal.

Best Regards,
Novatek Engineering Communications Bureau`);
  };

  const handleBroadcastTypeChange = (type) => {
    setBroadcastType(type);
    if (type === 'blog') {
      const b = blogs.find(item => item.id == selectedBlogId) || blogs[0];
      if (b) applyBlogTemplate(b);
    } else if (type === 'newsletter') {
      setSubject('Novatek Quarterly Technical Briefing: Industrial Automation & Aerospace Systems');
      setCustomBody(`Dear Partner / Subscriber,

Welcome to the Novatek Quarterly Technical Briefing.

In this edition:
• Advanced End-of-Line Railway Testing Rigs
• 6-Axis Robotic Cell Integrations in Aerospace Assembly
• Digital Twins for Predictive Load Cell Calibration

Explore our full product catalogue and request custom engineering specifications directly on our website.

Warm Regards,
Novatek Technical Operations Team`);
    } else if (type === 'greeting') {
      setSubject('Greetings from NOVATEK Industrial Systems Team!');
      setCustomBody(`Dear Esteemed Client & Engineer,

On behalf of the entire leadership and engineering team at NOVATEK Industrial Systems, we send our warmest greetings and express our appreciation for your partnership.

We remain dedicated to advancing your manufacturing precision, test bench reliability, and automation excellence.

Sincerely,
Dr. Aris Thorne & Novatek Bureau`);
    } else {
      setSubject('Important Announcement from Novatek Industrial Systems');
      setCustomBody('');
    }
  };

  const handleBlogSelectChange = (blogId) => {
    setSelectedBlogId(blogId);
    const b = blogs.find(item => item.id == blogId);
    if (b) applyBlogTemplate(b);
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!newEmailInput || !window.validateEmail(newEmailInput)) {
      alert('Please enter a valid email address.');
      return;
    }
    try {
      const res = await window.apiClient.post('subscribers.php', {
        email: newEmailInput.trim(),
        source: 'Admin Manual Import'
      });
      if (res && res.success !== false) {
        if (showNotification) showNotification(`Email '${newEmailInput}' added to subscribers list.`);
        setNewEmailInput('');
        setIsAddingSubscriber(false);
        fetchSubscribers();
      } else {
        alert(res.message || 'Subscriber addition failed.');
      }
    } catch (err) {
      alert(err.message || 'Failed to add subscriber.');
    }
  };

  const handleDeleteSubscriber = async (id, email) => {
    if (!window.confirm(`Remove '${email}' from subscribers list?`)) return;
    try {
      const res = await window.apiClient.delete('subscribers.php', { id });
      if (res && res.success !== false) {
        if (showNotification) showNotification(`Subscriber '${email}' removed.`);
        fetchSubscribers();
      } else {
        alert(res.message || 'Remove failed.');
      }
    } catch (err) {
      alert(err.message || 'Remove failed.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to remove the ${selectedIds.length} selected subscribers?`)) return;
    try {
      const res = await window.apiClient.request('subscribers.php', {
        method: 'DELETE',
        body: { ids: selectedIds }
      });
      if (res && res.success !== false) {
        if (showNotification) showNotification(`Successfully removed ${selectedIds.length} subscribers.`);
        setSelectedIds([]);
        fetchSubscribers();
      } else {
        alert(res.message || 'Bulk remove failed.');
      }
    } catch (err) {
      alert(err.message || 'Bulk remove failed.');
    }
  };

  const handleExportCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8,ID,Email Address,Source,Date Subscribed,Status\n" +
      subscribers.map(s => `"${s.id}","${s.email}","${s.source || 'Website'}","${s.created_at}","${s.status || 'Subscribed'}"`).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Novatek_Subscribers_${new Date().toISOString().substring(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLaunchMailApp = () => {
    if (!subject.trim() || !customBody.trim()) {
      alert('Please enter both Email Subject and Body Message.');
      return;
    }

    if (subscribers.length === 0) {
      alert('No subscribers in database.');
      return;
    }

    const bccEmails = subscribers.map(s => s.email).join(',');
    const mailBody = `${customBody}\n\n---\n${senderName || 'NOVATEK Industrial Systems'}\nRead publication online: ${window.location.origin}/#blogs`;
    const mailtoUrl = `mailto:?bcc=${encodeURIComponent(bccEmails)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailBody)}`;

    window.open(mailtoUrl, '_blank');
    if (showNotification) showNotification(`Opened Mail App with ${subscribers.length} subscriber emails pre-filled in BCC!`);
  };

  const selectedBlogObj = blogs.find(b => b.id == selectedBlogId) || blogs[0];

  const filteredSubscribers = subscribers.filter(s => 
    (s.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.source || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="font-serif text-2xl font-bold text-slate-900 flex items-center gap-2">
            <span>Newsletter & Bulk Email Dispatcher</span>
          </h1>
          <p className="font-sans text-xs text-slate-500">Collect subscriber emails from website forms, dispatch blog updates, technical newsletters, or greetings in bulk.</p>
        </div>

        {/* Tab Selector Buttons */}
        <div className="flex gap-2 bg-slate-100 p-1 rounded-xl border border-slate-200">
          <button
            onClick={() => setActiveTab('dispatcher')}
            className={`px-4 py-2 rounded-lg font-mono text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'dispatcher' ? 'bg-secondary text-white shadow' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span className="material-symbols-outlined text-sm">campaign</span>
            <span>Bulk Broadcast</span>
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`px-4 py-2 rounded-lg font-mono text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'subscribers' ? 'bg-secondary text-white shadow' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span className="material-symbols-outlined text-sm">contacts</span>
            <span>Subscribers List ({subscribers.length})</span>
          </button>
        </div>
      </div>

      {/* TAB 1: BULK BROADCAST DISPATCHER */}
      {activeTab === 'dispatcher' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Left Column: Broadcast Controls & Composer */}
          <div className="lg:col-span-7 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
            
            <div className="border-b border-slate-100 pb-3 flex justify-between items-center">
              <h3 className="font-serif text-lg font-bold text-slate-900 flex items-center gap-2">
                <span className="material-symbols-outlined text-secondary">mail</span>
                <span>Compose Bulk Email Campaign</span>
              </h3>
              <span className="font-mono text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded font-bold">
                {subscribers.length} Subscribers Ready
              </span>
            </div>

            {/* Sender Configuration Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
              <div>
                <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1">
                  SENDER NAME
                </label>
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="e.g. NOVATEK Engineering Bureau"
                  className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded text-xs text-slate-900 outline-none focus:border-secondary font-mono"
                />
              </div>

              <div>
                <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-1">
                  SENDER EMAIL ADDRESS *
                </label>
                <input
                  type="email"
                  required
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="e.g. newsletter@novatekindustrial.com"
                  className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded text-xs text-slate-900 outline-none focus:border-secondary font-mono"
                />
              </div>
            </div>

            {/* Campaign Preset Type */}
            <div>
              <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-2">
                SELECT BROADCAST TYPE
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => handleBroadcastTypeChange('blog')}
                  className={`p-3 rounded-xl border text-center transition-all cursor-pointer font-mono text-xs ${
                    broadcastType === 'blog' ? 'bg-secondary text-white border-secondary font-bold shadow' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span className="material-symbols-outlined block text-xl mb-1">newspaper</span>
                  <span>Share Blog</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleBroadcastTypeChange('newsletter')}
                  className={`p-3 rounded-xl border text-center transition-all cursor-pointer font-mono text-xs ${
                    broadcastType === 'newsletter' ? 'bg-secondary text-white border-secondary font-bold shadow' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span className="material-symbols-outlined block text-xl mb-1">mark_email_unread</span>
                  <span>Technical Briefing</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleBroadcastTypeChange('greeting')}
                  className={`p-3 rounded-xl border text-center transition-all cursor-pointer font-mono text-xs ${
                    broadcastType === 'greeting' ? 'bg-secondary text-white border-secondary font-bold shadow' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span className="material-symbols-outlined block text-xl mb-1">celebration</span>
                  <span>Greetings</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleBroadcastTypeChange('custom')}
                  className={`p-3 rounded-xl border text-center transition-all cursor-pointer font-mono text-xs ${
                    broadcastType === 'custom' ? 'bg-secondary text-white border-secondary font-bold shadow' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <span className="material-symbols-outlined block text-xl mb-1">edit_note</span>
                  <span>Custom Mail</span>
                </button>
              </div>
            </div>

            {/* Select Published Blog Post Dropdown */}
            {broadcastType === 'blog' && (
              <div>
                <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  SELECT PUBLISHED BLOG ARTICLE *
                </label>
                <select
                  value={selectedBlogId}
                  onChange={(e) => handleBlogSelectChange(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-mono text-xs outline-none focus:border-secondary"
                >
                  {blogs.length === 0 ? (
                    <option value="">No published blogs available</option>
                  ) : (
                    blogs.map((b) => (
                      <option key={b.id} value={b.id}>
                        [{b.category}] {b.title}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}

            {/* Subject Line */}
            <div>
              <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                EMAIL SUBJECT LINE *
              </label>
              <input
                type="text"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject line for bulk broadcast..."
                className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:border-secondary outline-none font-mono"
              />
            </div>

            {/* Message Body Content */}
            <div>
              <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                EMAIL MESSAGE CONTENT / BRIEFING DETAILS *
              </label>
              <textarea
                rows="8"
                required
                value={customBody}
                onChange={(e) => setCustomBody(e.target.value)}
                placeholder="Compose your newsletter message, announcement, or technical greeting..."
                className="w-full p-3.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-sans text-xs outline-none focus:border-secondary leading-relaxed custom-scrollbar"
              ></textarea>
            </div>

            {/* Action Bar */}
            <div className="pt-3 border-t border-slate-100 flex justify-end items-center">
              <button
                type="button"
                onClick={handleLaunchMailApp}
                className="w-full sm:w-auto px-8 py-3.5 bg-secondary hover:bg-orange-600 active:scale-95 text-white font-mono text-xs font-bold uppercase rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span className="material-symbols-outlined text-lg">open_in_new</span>
                <span>Send Mail via Email App (BCC All {subscribers.length} Subscribers)</span>
              </button>
            </div>

          </div>

          {/* Right Column: Live Email Card Preview */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-3">
              <div className="flex justify-between items-center border-b border-slate-100 pb-2.5">
                <span className="font-mono text-[11px] font-bold text-slate-600 uppercase tracking-wider">LIVE EMAIL PREVIEW</span>
                <span className="text-[10px] font-mono text-secondary font-bold">HTML Responsive Card</span>
              </div>

              {/* Email Client Wrapper Box */}
              <div className="bg-slate-50 rounded-2xl p-5 text-slate-800 shadow-inner space-y-4 border border-slate-200 text-xs font-sans">
                
                {/* Header Brand */}
                <div className="flex justify-between items-center border-b border-slate-200 pb-3">
                  <img src="/logo.png" alt="NOVATEK" className="h-7 w-auto" />
                  <span className="font-mono text-[10px] text-slate-500">Technical Bureau Briefing</span>
                </div>

                {/* From / Sender Information */}
                <div className="bg-white p-2.5 rounded-lg border border-slate-200 font-mono text-[11px] space-y-0.5 shadow-xs">
                  <div><span className="text-slate-400 font-bold">FROM:</span> <span className="text-slate-900 font-bold">{senderName || 'NOVATEK'}</span> &lt;<span className="text-secondary font-bold">{senderEmail || 'newsletter@novatekindustrial.com'}</span>&gt;</div>
                  <div><span className="text-slate-400 font-bold">TO:</span> <span className="text-slate-700">Subscriber List ({subscribers.length} Recipients)</span></div>
                </div>

                {/* Email Subject Header */}
                <div>
                  <span className="text-[10px] font-mono text-slate-400 block uppercase font-bold">SUBJECT:</span>
                  <h4 className="font-serif font-bold text-base text-slate-900 leading-snug">
                    {subject || 'No subject line specified'}
                  </h4>
                </div>

                {/* Blog Featured Cover Photo (If Blog Type) */}
                {broadcastType === 'blog' && selectedBlogObj && (
                  <div className="rounded-xl overflow-hidden bg-slate-900 h-40 border border-slate-200 relative">
                    <img src={selectedBlogObj.image} alt={selectedBlogObj.title} className="w-full h-full object-cover" />
                    <span className="absolute top-2 left-2 bg-orange-600 text-white font-mono text-[9px] font-bold px-2 py-0.5 rounded uppercase">
                      {selectedBlogObj.category}
                    </span>
                  </div>
                )}

                {/* Body Content */}
                <div className="text-slate-700 leading-relaxed whitespace-pre-line bg-white p-4 rounded-xl border border-slate-200 font-sans shadow-xs">
                  {customBody || 'Compose message body to see preview...'}
                </div>

                {/* Call to Action Button */}
                <div className="text-center pt-2">
                  <a
                    href="#blogs"
                    onClick={(e) => e.preventDefault()}
                    className="inline-block bg-slate-900 text-white font-mono text-xs font-bold px-6 py-2.5 rounded-lg shadow"
                  >
                    View Publication on Website →
                  </a>
                </div>

                {/* Footer Disclaimer */}
                <div className="border-t border-slate-200 pt-3 text-[10px] font-mono text-slate-400 text-center space-y-1">
                  <div>© NOVATEK Industrial Systems • Technology Park Sector 4, India</div>
                  <div>You received this briefing because your email is registered on our technical portal.</div>
                </div>

              </div>
            </div>
          </div>

        </div>
      )}

      {/* TAB 2: SUBSCRIBERS DIRECTORY MANAGER */}
      {activeTab === 'subscribers' && (
        <div className="space-y-6">
          
          {/* Controls Bar */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
            
            <div className="relative w-full sm:w-80">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
              <input
                type="text"
                placeholder="Search by subscriber email or source..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:border-secondary font-mono"
              />
            </div>

            <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-end">
              {selectedIds.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 text-white font-mono text-xs font-bold px-4 py-2 rounded-lg shadow flex items-center gap-1.5 cursor-pointer animate-fadeIn"
                >
                  <span className="material-symbols-outlined text-sm">delete_sweep</span>
                  <span>Delete Selected ({selectedIds.length})</span>
                </button>
              )}

              <button
                onClick={() => setIsAddingSubscriber(true)}
                className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold px-4 py-2 rounded-lg shadow flex items-center gap-1.5 cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">person_add</span>
                <span>Add Subscriber</span>
              </button>

              <button
                onClick={handleExportCSV}
                className="bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-mono text-xs font-bold px-4 py-2 rounded-lg shadow-xs flex items-center gap-1.5 cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">download</span>
                <span>Export CSV</span>
              </button>
            </div>

          </div>

          {/* Add Subscriber Form Modal / Inline Box */}
          {isAddingSubscriber && (
            <div className="bg-white p-5 rounded-2xl border border-secondary/40 shadow-lg space-y-3 animate-fadeIn">
              <div className="flex justify-between items-center">
                <h4 className="font-serif font-bold text-slate-900 text-sm">Add New Email Subscriber Manually</h4>
                <button onClick={() => setIsAddingSubscriber(false)} className="text-slate-400 hover:text-slate-800">
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              </div>
              <form onSubmit={handleAddSubscriber} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={newEmailInput}
                  onChange={(e) => setNewEmailInput(e.target.value)}
                  placeholder="enter.engineer@company.com"
                  className="flex-grow px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-mono outline-none focus:border-secondary"
                />
                <button
                  type="submit"
                  className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold px-5 py-2 rounded-lg shrink-0 cursor-pointer"
                >
                  Save Subscriber
                </button>
              </form>
            </div>
          )}

          {/* Subscribers Table */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-sans">
                <thead className="bg-slate-50 text-slate-500 font-mono text-[10px] uppercase border-b border-slate-200">
                  <tr>
                    <th className="p-4 w-10 text-center">
                      <input 
                        type="checkbox" 
                        checked={filteredSubscribers.length > 0 && selectedIds.length === filteredSubscribers.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(filteredSubscribers.map(sub => sub.id));
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                        className="cursor-pointer h-4 w-4 rounded border-slate-300 text-secondary focus:ring-secondary"
                      />
                    </th>
                    <th className="p-4">SUBSCRIBER EMAIL ADDRESS</th>
                    <th className="p-4">ACQUISITION SOURCE</th>
                    <th className="p-4">SUBSCRIPTION DATE</th>
                    <th className="p-4">STATUS</th>
                    <th className="p-4 text-center">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-mono">
                  {filteredSubscribers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-slate-400 font-mono">No email subscribers found matching search.</td>
                    </tr>
                  ) : (
                    filteredSubscribers.map((sub) => (
                      <tr key={sub.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.includes(sub.id) ? 'bg-orange-50/40' : ''}`}>
                        <td className="p-4 w-10 text-center">
                          <input 
                            type="checkbox" 
                            checked={selectedIds.includes(sub.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(prev => [...prev, sub.id]);
                              } else {
                                setSelectedIds(prev => prev.filter(id => id !== sub.id));
                              }
                            }}
                            className="cursor-pointer h-4 w-4 rounded border-slate-300 text-secondary focus:ring-secondary"
                          />
                        </td>
                        <td className="p-4 font-bold text-slate-900 text-sm">{sub.email}</td>
                        <td className="p-4 text-slate-600 text-[11px]">{sub.source || 'Website Footer'}</td>
                        <td className="p-4 text-slate-500 text-[11px]">{sub.created_at}</td>
                        <td className="p-4">
                          <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono text-[10px] font-bold px-2.5 py-0.5 rounded uppercase">
                            {sub.status || 'Active'}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <button
                            onClick={() => handleDeleteSubscriber(sub.id, sub.email)}
                            className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200 cursor-pointer"
                            title="Remove Subscriber"
                          >
                            <span className="material-symbols-outlined text-sm">delete</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

    </div>
  );
};
